import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { calculateAreaHectares, validatePolygon, snapToVertex, snapPointToPolygonBoundary, snapPointToMultiplePolygons } from './utils/polygonCalculations';

// Set Mapbox Access Token
mapboxgl.accessToken = 'pk.eyJ1IjoiZW1pbGtqYW5kZXJzc29uIiwiYSI6ImNsaXZleTAyNzAxc2oza3FsZWhzazVxMHYifQ.m_Rv4YYKdB0uR22jKdJ5MQ';

const SnappedPolygonMode = Object.assign({}, MapboxDraw.modes.draw_polygon);

SnappedPolygonMode.onSetup = function(opts) {
    const state = MapboxDraw.modes.draw_polygon.onSetup.call(this, opts);
    state.farmBoundary = opts.farmBoundary;
    state.forestStands = opts.forestStands?.features || opts.forestStands || [];
    // Set a property so we can style it differently
    state.polygon.setProperty('isStand', 'true');
    
    // Set custom point cursor when starting to draw
    if (this.map) {
        const svg = `
            <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="4" fill="#228B22" stroke="white" stroke-width="1.5"/>
            </svg>
        `;
        const cursorUrl = `data:image/svg+xml;base64,${btoa(svg)}`;
        this.map.getCanvas().style.cursor = `url(${cursorUrl}) 8 8, crosshair`;
    }
    
    return state;
};

SnappedPolygonMode.onStop = function(state) {
    MapboxDraw.modes.draw_polygon.onStop.call(this, state);
    // Reset cursor when stopping drawing
    if (this.map) {
        this.map.getCanvas().style.cursor = '';
    }
};

SnappedPolygonMode.onMouseMove = function(state, e) {
    if (state.farmBoundary || (state.forestStands && state.forestStands.length > 0)) {
        try {
            const snapped = snapPointToMultiplePolygons(
                [e.lngLat.lng, e.lngLat.lat], 
                state.forestStands, 
                state.farmBoundary
            );
            e.lngLat.lng = snapped[0];
            e.lngLat.lat = snapped[1];
        } catch (err) {
            console.error("Snapping error in onMouseMove", err);
        }
    }
    MapboxDraw.modes.draw_polygon.onMouseMove.call(this, state, e);
    
    // Force redraw of the active feature to ensure preview line snaps
    if (state.polygon) {
        const vertexIndex = state.currentVertexPosition || (state.polygon.coordinates[0].length - 1);
        const snapped = snapPointToMultiplePolygons(
            state.polygon.coordinates[0][vertexIndex], 
            state.forestStands, 
            state.farmBoundary
        );
        state.polygon.updateCoordinate(`0.${vertexIndex}`, snapped[0], snapped[1]);
        state.polygon.changed();
    }
};

SnappedPolygonMode.onClick = function(state, e) {
    if (state.farmBoundary || (state.forestStands && state.forestStands.length > 0)) {
        try {
            const snapped = snapPointToMultiplePolygons(
                [e.lngLat.lng, e.lngLat.lat], 
                state.forestStands, 
                state.farmBoundary
            );
            e.lngLat.lng = snapped[0];
            e.lngLat.lat = snapped[1];
        } catch (err) {
            console.error("Snapping error in onClick", err);
        }
    }
    
    MapboxDraw.modes.draw_polygon.onClick.call(this, state, e);
    
    // Ensure the newly added vertex is also snapped
    if (state.polygon && (state.farmBoundary || (state.forestStands && state.forestStands.length > 0))) {
        const vertexIndex = state.polygon.coordinates[0].length - 1;
        const snapped = snapPointToMultiplePolygons(
            state.polygon.coordinates[0][vertexIndex], 
            state.forestStands, 
            state.farmBoundary
        );
        state.polygon.updateCoordinate(`0.${vertexIndex}`, snapped[0], snapped[1]);
        state.polygon.changed();
    }
};

SnappedPolygonMode.toDisplayFeatures = function(state, geojson, display) {
    const wrappedDisplay = (feature) => {
        feature.properties.user_isStand = 'true';
        display(feature);
    };
    MapboxDraw.modes.draw_polygon.toDisplayFeatures.call(this, state, geojson, wrappedDisplay);
};

SnappedPolygonMode.onTap = function(state, e) {
    this.onClick(state, e);
};

const MapComponent = ({ onPolygonUpdate, onPolygonSelect, mode = 'view', farmBoundary, forestStands, onForestStandCreate, onForestStandSelect, selectedStandId, highlightedStandIds = [] }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  
  // Use refs for handlers to avoid stale closures in event listeners
  const handlersRef = useRef({ mode, farmBoundary, onForestStandCreate, onPolygonUpdate, onPolygonSelect, onForestStandSelect });
  useEffect(() => {
    handlersRef.current = { mode, farmBoundary, onForestStandCreate, onPolygonUpdate, onPolygonSelect, onForestStandSelect };
  }, [mode, farmBoundary, onForestStandCreate, onPolygonUpdate, onPolygonSelect, onForestStandSelect]);

  const [lng, setLng] = useState(15.0);
  const [lat, setLat] = useState(62.0);
  const [zoom, setZoom] = useState(5);
  const [selectedFeature, setSelectedFeature] = useState(null);

  useEffect(() => {
    if (map.current) return;

    if (!mapboxgl.supported()) {
        console.error('Mapbox GL JS is not supported in this browser');
        alert('Mapbox GL JS is not supported in this browser');
        return;
    }

    try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/satellite-streets-v12',
          center: [lng, lat],
          zoom: zoom,
          trackResize: true
        });

        setTimeout(() => {
            if (map.current && map.current.getCanvas()) map.current.resize();
        }, 1000);

        map.current.on('load', () => {
            console.log("Map loaded, setting initial data");
            setIsStyleLoaded(true);
            map.current.resize();

            // Click listener for stand selection
            map.current.on('click', 'forest-stands-fill', (e) => {
                const { mode, onForestStandSelect } = handlersRef.current;
                if (mode === 'draw_stand') return; // Disable selection while drawing
                
                if (e.features.length > 0) {
                    const feature = e.features[0];
                    console.log("Stand clicked:", feature.id, feature.properties);
                    if (onForestStandSelect) onForestStandSelect(feature);
                    
                    // Update highlight filter
                    map.current.setFilter('forest-stands-highlight', ['==', 'id', feature.id || '']);
                }
            });

            // Change cursor on hover
            map.current.on('mouseenter', 'forest-stands-fill', () => {
                const { mode } = handlersRef.current;
                if (mode === 'draw_stand') return;
                map.current.getCanvas().style.cursor = 'pointer';
            });
            map.current.on('mouseleave', 'forest-stands-fill', () => {
                const { mode } = handlersRef.current;
                if (mode === 'draw_stand') return;
                map.current.getCanvas().style.cursor = '';
            });

            // Add source and layer for static farm boundary
            map.current.addSource('farm-boundary', {
                type: 'geojson',
                data: farmBoundary || { type: 'FeatureCollection', features: [] }
            });

            map.current.addLayer({
                id: 'farm-boundary-glow',
                type: 'line',
                source: 'farm-boundary',
                layout: {},
                paint: {
                    'line-color': '#FFD700',
                    'line-width': 8,
                    'line-blur': 4,
                    'line-opacity': 0.4
                }
            });

            map.current.addLayer({
                id: 'farm-boundary-line',
                type: 'line',
                source: 'farm-boundary',
                layout: {},
                paint: {
                    'line-color': '#FFD700', // Gold color
                    'line-width': 4
                }
            });

            // Add source and layer for forest stands
            map.current.addSource('forest-stands', {
                type: 'geojson',
                data: forestStands || { type: 'FeatureCollection', features: [] }
            });

            // Ensure data is pushed when style loads (fixes visibility after refresh)
            map.current.on('style.load', () => {
                console.log("Style reloaded, re-adding sources and layers");
                setIsStyleLoaded(false); // Trigger re-addition
                
                // Re-add sources if they are gone
                if (!map.current.getSource('farm-boundary')) {
                    map.current.addSource('farm-boundary', {
                        type: 'geojson',
                        data: farmBoundary || { type: 'FeatureCollection', features: [] }
                    });
                }
                if (!map.current.getSource('forest-stands')) {
                    map.current.addSource('forest-stands', {
                        type: 'geojson',
                        data: forestStands || { type: 'FeatureCollection', features: [] }
                    });
                }
                
                // Re-add layers if they are gone
                if (!map.current.getLayer('farm-boundary-line')) {
                    map.current.addLayer({
                        id: 'farm-boundary-glow',
                        type: 'line',
                        source: 'farm-boundary',
                        paint: {
                            'line-color': '#FFD700',
                            'line-width': 8,
                            'line-blur': 4,
                            'line-opacity': 0.4
                        }
                    });
                    map.current.addLayer({
                        id: 'farm-boundary-line',
                        type: 'line',
                        source: 'farm-boundary',
                        paint: {
                            'line-color': '#FFD700',
                            'line-width': 4
                        }
                    });
                }
                
                if (!map.current.getLayer('forest-stands-fill')) {
                    map.current.addLayer({
                        id: 'forest-stands-fill',
                        type: 'fill',
                        source: 'forest-stands',
                        paint: {
                            'fill-color': '#228B22',
                            'fill-opacity': 0.4
                        }
                    });
                    map.current.addLayer({
                        id: 'forest-stands-line',
                        type: 'line',
                        source: 'forest-stands',
                        paint: {
                            'line-color': '#006400',
                            'line-width': 2
                        }
                    });

                    // Add highlight layer for selected stand
                    map.current.addLayer({
                        id: 'forest-stands-highlight',
                        type: 'line',
                        source: 'forest-stands',
                        paint: {
                            'line-color': '#fff',
                            'line-width': 4
                        },
                        filter: ['==', 'id', '']
                    });

                    // Add labels layer
                    map.current.addLayer({
                        id: 'forest-stands-labels',
                        type: 'symbol',
                        source: 'forest-stands',
                        layout: {
                            'text-field': ['get', 'stand_number'],
                            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                            'text-size': 14,
                            'text-anchor': 'center',
                            'text-allow-overlap': false
                        },
                        paint: {
                            'text-color': '#ffffff',
                            'text-halo-color': '#000000',
                            'text-halo-width': 2
                        }
                    });

                    // Add activity highlight layer (orange/yellow highlight for filtered activities)
                    map.current.addLayer({
                        id: 'forest-stands-activity-highlight',
                        type: 'fill',
                        source: 'forest-stands',
                        paint: {
                            'fill-color': '#FFA500',
                            'fill-opacity': 0.3
                        },
                        filter: ['in', 'id', '']
                    });
                }

                setIsStyleLoaded(true);
            });

            map.current.addLayer({
                id: 'forest-stands-fill',
                type: 'fill',
                source: 'forest-stands',
                paint: {
                    'fill-color': '#228B22', // Forest Green
                    'fill-opacity': 0.4
                }
            });
            
            map.current.addLayer({
                id: 'forest-stands-line',
                type: 'line',
                source: 'forest-stands',
                paint: {
                    'line-color': '#006400', // Dark Green
                    'line-width': 2
                }
            });

            // Add highlight layer for selected stand (global)
            map.current.addLayer({
                id: 'forest-stands-highlight',
                type: 'line',
                source: 'forest-stands',
                paint: {
                    'line-color': '#fff',
                    'line-width': 4
                },
                filter: ['==', 'id', '']
            });

            // Add labels layer
            map.current.addLayer({
                id: 'forest-stands-labels',
                type: 'symbol',
                source: 'forest-stands',
                layout: {
                    'text-field': ['get', 'stand_number'],
                    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                    'text-size': 14,
                    'text-anchor': 'center',
                    'text-allow-overlap': false
                },
                paint: {
                    'text-color': '#ffffff',
                    'text-halo-color': '#000000',
                    'text-halo-width': 2
                }
            });

            // Add activity highlight layer (orange/yellow highlight for filtered activities)
            map.current.addLayer({
                id: 'forest-stands-activity-highlight',
                type: 'fill',
                source: 'forest-stands',
                paint: {
                    'fill-color': '#FFA500',
                    'fill-opacity': 0.3
                },
                filter: ['in', 'id', '']
            });
            
            if (farmBoundary && farmBoundary.geometry) {
                const bounds = new mapboxgl.LngLatBounds();
                const coords = farmBoundary.geometry.type === 'MultiPolygon' 
                    ? farmBoundary.geometry.coordinates.flat(1) 
                    : farmBoundary.geometry.coordinates;
                coords[0].forEach(coord => bounds.extend(coord));
                map.current.fitBounds(bounds, { padding: 50 });
                map.current._centeredOnFarm = true;
            }
        });

    } catch (err) {
        console.error('Error initializing map:', err);
    }

    draw.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true
      },
      defaultMode: 'simple_select',
      userProperties: true,
      modes: {
        ...MapboxDraw.modes,
        snapped_polygon: SnappedPolygonMode
      },
      styles: [
          {
            'id': 'gl-draw-polygon-fill-inactive',
            'type': 'fill',
            'filter': ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
            'paint': {
              'fill-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#228B22',
                '#FFD700'
              ],
              'fill-outline-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#006400',
                '#FFD700'
              ],
              'fill-opacity': 0.1
            }
          },
          {
            'id': 'gl-draw-polygon-fill-active',
            'type': 'fill',
            'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            'paint': {
              'fill-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#228B22',
                '#FFD700'
              ],
              'fill-outline-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#006400',
                '#FFD700'
              ],
              'fill-opacity': 0.1
            }
          },
          {
            'id': 'gl-draw-polygon-stroke-inactive',
            'type': 'line',
            'filter': ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
            'layout': {
              'line-cap': 'round',
              'line-join': 'round'
            },
            'paint': {
              'line-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#228B22',
                '#FFD700'
              ],
              'line-width': 2
            }
          },
          {
            'id': 'gl-draw-polygon-stroke-active',
            'type': 'line',
            'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            'layout': {
              'line-cap': 'round',
              'line-join': 'round'
            },
            'paint': {
              'line-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#228B22',
                '#FFD700'
              ],
              'line-dasharray': [0.2, 2],
              'line-width': 2
            }
          },
          {
            'id': 'gl-draw-line-inactive',
            'type': 'line',
            'filter': ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
            'layout': {
              'line-cap': 'round',
              'line-join': 'round'
            },
            'paint': {
              'line-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#228B22',
                '#FFD700'
              ],
              'line-width': 2
            }
          },
          {
            'id': 'gl-draw-line-active',
            'type': 'line',
            'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'LineString']],
            'layout': {
              'line-cap': 'round',
              'line-join': 'round'
            },
            'paint': {
              'line-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#228B22',
                '#FFD700'
              ],
              'line-dasharray': [0.2, 2],
              'line-width': 2
            }
          },
          {
            'id': 'gl-draw-polygon-and-line-vertex-stroke-active',
            'type': 'circle',
            'filter': ['all', ['==', 'meta', 'vertex'], ['==', 'active', 'true'], ['!=', 'mode', 'static']],
            'paint': {
              'circle-radius': 5,
              'circle-color': '#fff'
            }
          },
          {
            'id': 'gl-draw-polygon-and-line-vertex-active',
            'type': 'circle',
            'filter': ['all', ['==', 'meta', 'vertex'], ['==', 'active', 'true'], ['!=', 'mode', 'static']],
            'paint': {
              'circle-radius': 3,
              'circle-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#228B22',
                '#FFD700'
              ]
            }
          },
          {
            'id': 'gl-draw-polygon-and-line-vertex-stroke-inactive',
            'type': 'circle',
            'filter': ['all', ['==', 'meta', 'vertex'], ['==', 'active', 'false'], ['!=', 'mode', 'static']],
            'paint': {
              'circle-radius': 3,
              'circle-color': '#fff'
            }
          },
          {
            'id': 'gl-draw-polygon-and-line-vertex-inactive',
            'type': 'circle',
            'filter': ['all', ['==', 'meta', 'vertex'], ['==', 'active', 'false'], ['!=', 'mode', 'static']],
            'paint': {
              'circle-radius': 3,
              'circle-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#228B22',
                '#FFD700'
              ]
            }
          },
          {
            'id': 'gl-draw-point-point-stroke-inactive',
            'type': 'circle',
            'filter': ['all', ['==', 'active', 'false'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
            'paint': {
              'circle-radius': 5,
              'circle-opacity': 1,
              'circle-color': '#fff'
            }
          },
          {
            'id': 'gl-draw-point-inactive',
            'type': 'circle',
            'filter': ['all', ['==', 'active', 'false'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
            'paint': {
              'circle-radius': 3,
              'circle-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#228B22',
                '#FFD700'
              ]
            }
          },
          {
            'id': 'gl-draw-point-stroke-active',
            'type': 'circle',
            'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'Point']],
            'paint': {
              'circle-radius': 7,
              'circle-color': '#fff'
            }
          },
          {
            'id': 'gl-draw-point-active',
            'type': 'circle',
            'filter': ['all', ['==', 'active', 'true'], ['==', '$type', 'Point']],
            'paint': {
              'circle-radius': 5,
              'circle-color': [
                'case',
                ['==', ['get', 'user_isStand'], 'true'], '#228B22',
                '#FFD700'
              ]
            }
          },
          {
            'id': 'gl-draw-polygon-fill-static',
            'type': 'fill',
            'filter': ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']],
            'paint': {
              'fill-color': '#404040',
              'fill-outline-color': '#404040',
              'fill-opacity': 0.1
            }
          },
          {
            'id': 'gl-draw-polygon-stroke-static',
            'type': 'line',
            'filter': ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']],
            'layout': {
              'line-cap': 'round',
              'line-join': 'round'
            },
            'paint': {
              'line-color': '#404040',
              'line-width': 2
            }
          },
          {
            'id': 'gl-draw-line-static',
            'type': 'line',
            'filter': ['all', ['==', 'mode', 'static'], ['==', '$type', 'LineString']],
            'layout': {
              'line-cap': 'round',
              'line-join': 'round'
            },
            'paint': {
              'line-color': '#404040',
              'line-width': 2
            }
          },
          {
            'id': 'gl-draw-point-static',
            'type': 'circle',
            'filter': ['all', ['==', 'mode', 'static'], ['==', '$type', 'Point']],
            'paint': {
              'circle-radius': 5,
              'circle-color': '#404040'
            }
          }
      ]
    });

    map.current.addControl(draw.current);

    map.current.on('draw.create', handleDrawCreate);
    map.current.on('draw.delete', updateArea);
    map.current.on('draw.update', handleDrawUpdate);
    map.current.on('draw.selectionchange', handleSelection);

    return () => map.current.remove();
  }, []);

  // Reset centering flag when farmBoundary changes (e.g. switching farms)
  useEffect(() => {
    if (map.current) {
        map.current._centeredOnFarm = false;
    }
  }, [farmBoundary]);

  // Effect to handle modes and farm boundary
  useEffect(() => {
      if (!map.current || !isStyleLoaded) return;

      const farmSource = map.current.getSource('farm-boundary');
      const standSource = map.current.getSource('forest-stands');
      
      // Update static layer data
      if (farmSource) {
          console.log("Setting farm boundary data. Features:", farmBoundary?.features?.length || (farmBoundary?.geometry ? 1 : 0));
          farmSource.setData(farmBoundary || { type: 'FeatureCollection', features: [] });
          
          // Center map on farm if it exists and we just switched to a mode that needs it, or if it's the first load
          if (farmBoundary && farmBoundary.geometry && (mode === 'draw_stand' || !map.current._centeredOnFarm)) {
             const bounds = new mapboxgl.LngLatBounds();
             // Handle both Polygon and MultiPolygon
             const coords = farmBoundary.geometry.type === 'MultiPolygon' 
                ? farmBoundary.geometry.coordinates.flat(1) 
                : farmBoundary.geometry.coordinates;
                
             coords[0].forEach(coord => {
                 bounds.extend(coord);
             });
             map.current.fitBounds(bounds, { padding: 50 });
             map.current._centeredOnFarm = true;
          }
      }

      if (standSource) {
          standSource.setData(forestStands || { type: 'FeatureCollection', features: [] });
      }

      // Handle Modes
      if (mode === 'edit_farm') {
          // Edit Farm Mode: Add farm boundary to Draw, clear static
          if (draw.current.getMode() !== 'simple_select' && draw.current.getMode() !== 'direct_select') {
              if (farmBoundary) {
                  draw.current.add(farmBoundary);
                  if (farmSource) farmSource.setData({ type: 'FeatureCollection', features: [] });
                  setTimeout(() => {
                      if (draw.current) draw.current.changeMode('simple_select');
                  }, 0);
              } else {
                  // If no boundary, trigger draw mode immediately
                  setTimeout(() => {
                      if (draw.current) draw.current.changeMode('draw_polygon');
                  }, 0);
              }
          }
      } else if (mode === 'draw_stand') {
          // Draw Stand Mode: Farm boundary static, Draw active for new stands
          if (draw.current.getMode() !== 'snapped_polygon') {
              console.log("Switching to snapped_polygon mode");
              draw.current.deleteAll(); // Clear any previous drawings
              draw.current.changeMode('snapped_polygon', { 
                  farmBoundary, 
                  forestStands: forestStands?.features || [] 
              });
          }
      } else {
          // View Mode: Farm boundary static, Draw cleared
          draw.current.deleteAll();
          if (farmSource && farmBoundary) farmSource.setData(farmBoundary);
          if (draw.current.getMode() !== 'simple_select') {
              draw.current.changeMode('simple_select');
          }
      }
      
      // Always ensure stand source is updated
      if (standSource && forestStands) {
          console.log("Updating forest stands source with:", forestStands.features.length, "features");
          standSource.setData(forestStands);
          
          // Ensure layer is visible
          if (map.current.getLayer('forest-stands-fill')) {
              map.current.setLayoutProperty('forest-stands-fill', 'visibility', 'visible');
              map.current.setLayoutProperty('forest-stands-line', 'visibility', 'visible');
          }
      }
  }, [mode, farmBoundary, forestStands, isStyleLoaded]);

  // Sync highlight filter with selectedStandId
  useEffect(() => {
      if (map.current && isStyleLoaded) {
          console.log("Syncing highlight filter for stand:", selectedStandId);
          map.current.setFilter('forest-stands-highlight', ['==', 'id', selectedStandId || '']);
      }
  }, [selectedStandId, isStyleLoaded]);

  // Update activity highlight layer when highlightedStandIds changes
  useEffect(() => {
      if (map.current && isStyleLoaded) {
          if (highlightedStandIds.length > 0) {
              map.current.setFilter('forest-stands-activity-highlight', ['in', 'id', ...highlightedStandIds]);
          } else {
              map.current.setFilter('forest-stands-activity-highlight', ['in', 'id', '']);
          }
      }
  }, [highlightedStandIds, isStyleLoaded]);

  const handleDrawCreate = (e) => {
    const { mode, farmBoundary, onForestStandCreate } = handlersRef.current;
    let newFeature = e.features[0];
    console.log("handleDrawCreate triggered. Mode:", mode, "ID:", newFeature.id);
    
    // Snap entire polygon to farm boundary if drawing stand
    if (mode === 'draw_stand' && farmBoundary) {
        console.log("Snapping new stand to farm boundary");
        const coords = newFeature.geometry.coordinates[0];
        const snappedCoords = coords.map(coord => {
            return snapPointToPolygonBoundary(coord, farmBoundary.geometry);
        });
        newFeature = {
            ...newFeature,
            properties: { ...newFeature.properties, isStand: 'true' },
            geometry: {
                ...newFeature.geometry,
                coordinates: [snappedCoords]
            }
        };
        // Update in draw so user sees the snap and color
        draw.current.add(newFeature);
    }

    if (!validatePolygon(newFeature)) {
      alert('Invalid polygon: Self-intersection detected.');
      draw.current.delete(newFeature.id);
      return;
    }

    if (mode === 'draw_stand' && onForestStandCreate) {
        console.log("Calling onForestStandCreate");
        const areaHectares = calculateAreaHectares(newFeature);
        onForestStandCreate(newFeature, areaHectares);
    }

    updateArea();
  };

  const handleDrawUpdate = (e) => {
    const { mode, farmBoundary } = handlersRef.current;
    const updatedFeature = e.features[0];
    console.log("handleDrawUpdate triggered. Mode:", mode, "ID:", updatedFeature.id);
    
    // Snapping/Constraint Logic
    if (mode === 'draw_stand' && farmBoundary) {
        const coords = updatedFeature.geometry.coordinates[0];
        const snappedCoords = coords.map(coord => {
            return snapPointToPolygonBoundary(coord, farmBoundary.geometry);
        });

        if (JSON.stringify(coords) !== JSON.stringify(snappedCoords) || updatedFeature.properties.isStand !== 'true') {
             const newFeature = {
                 ...updatedFeature,
                 properties: { ...updatedFeature.properties, isStand: 'true' },
                 geometry: {
                     ...updatedFeature.geometry,
                     coordinates: [snappedCoords]
                 }
             };
             draw.current.add(newFeature);
        }
    }

    if (!validatePolygon(updatedFeature)) {
       console.warn('Invalid polygon shape');
    }
    updateArea();
  };

  const updateArea = () => {
    const { onPolygonUpdate } = handlersRef.current;
    const data = draw.current.getAll();
    if (data.features.length > 0) {
      // Calculate total area of all features
      let totalArea = 0;
      data.features.forEach(f => {
          totalArea += calculateAreaHectares(f);
      });
      
      if (onPolygonUpdate) {
        onPolygonUpdate(totalArea, data);
      }
    } else {
        if (onPolygonUpdate) onPolygonUpdate(0, { type: 'FeatureCollection', features: [] });
    }
  };

  const handleSelection = (e) => {
    const { onPolygonSelect } = handlersRef.current;
    if (e.features.length > 0) {
      if (onPolygonSelect) {
        onPolygonSelect(e.features[0]);
      }
    } else {
      if (onPolygonSelect) onPolygonSelect(null);
    }
  };

  return (
    <div className="map-wrap" style={{ position: 'relative', width: '100%', height: '600px', backgroundColor: '#e0e0e0' }}>
      <div ref={mapContainer} className="map" style={{ position: 'absolute', top: 0, bottom: 0, width: '100%', height: '100%' }} />
    </div>
  );
};

export default MapComponent;
